import {
  CustomEmbedRendererProps,
  DefaultRenderer,
  defaultRenderers,
  IEmbedNodeRendererProps,
  INodeRenderer,
  RealRTF,
  RealRTFProps,
} from "@bond-london/graphcms-rich-text";
import classNames from "classnames";
import React from "react";
import {
  PageEmbedRenderer,
  PageEmbedRendererProps,
} from "./renderers/PageEmbedRenderer";
import { PdfRenderer, PdfRendererProps } from "./renderers/PDFRenderer";
import { RTFLinkRenderer } from "./renderers/RTFLinkRenderer";
import { VideoRenderer } from "./renderers/VideoRenderer";
import { RTFButtonLinkRenderer } from "./renderers/RTFButtonLinkRenderer";

export type AssetRendererProps = CustomEmbedRendererProps<{
  id: string;
  alternateText?: string;
  localFile: { publicUrl: string };
}>;

const baseProjectRenderers: Partial<INodeRenderer> = {
  a: (props) => <RTFLinkRenderer props={props} />,
  p: (props) => <DefaultRenderer {...props} element="div" />,
  embed_asset: {
    ...defaultRenderers.embed_asset,
    video: (props) => (
      <VideoRenderer {...(props as unknown as AssetRendererProps)} />
    ),
    "application/pdf": (props) => {
      return <PdfRenderer {...(props as unknown as PdfRendererProps)} />;
    },
  },
  embed_node: {
    ...defaultRenderers.embed_node,
    Page: (props: IEmbedNodeRendererProps) => {
      return (
        <PageEmbedRenderer
          {...({
            ...props.reference,
            isInline: props.isInline ?? false,
          } as unknown as PageEmbedRendererProps)}
        />
      );
    },
    ButtonLink: (props: IEmbedNodeRendererProps) => {
      return <RTFButtonLinkRenderer {...(props.reference as any)} />;
    },
  },
  table: ({ children }) => (
    <table className="col-span-full break-all">{children}</table>
  ),
  table_head: ({ children }) => (
    <thead className="inset-block-start">{children}</thead>
  ),
  table_body: ({ children }) => <tbody>{children}</tbody>,
  table_row: ({ children }) => (
    <tr
      className="grid grid-flow-col px-10px py-10px"
      style={{ gridTemplateColumns: "3fr 2fr 2fr 2fr" }}
    >
      {children}
    </tr>
  ),
  table_cell: ({ children }) => (
    <td className="flex items-center px-5px lg:px-10px">{children}</td>
  ),
  class: {
    "left-1/3": { description: "", className: "w-1/3" },
    "left-1/2": { description: "", className: "w-1/2" },
    "left-2/3": { description: "", className: "w-2/3" },
    "center-1/3": { description: "", className: "w-1/3 mx-auto" },
    "center-1/2": { description: "", className: "w-1/2 mx-auto" },
    "center-2/3": { description: "", className: "w-2/3 mx-auto" },
    "right-1/3": { description: "", className: "w-1/3 ml-auto" },
    "right-1/2": { description: "", className: "w-1/2 ml-auto" },
    "right-2/3": { description: "", className: "w-2/3 ml-auto" },
  },
};

export const RTF: React.FC<RealRTFProps> = (props) => {
  const { className, ...rest } = props;
  return (
    <RealRTF
      {...rest}
      className={classNames(className, "rich-text")}
      projectRenderers={{ ...defaultRenderers, ...baseProjectRenderers }}
    />
  );
};
